import React, { useContext, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { format, type Locale } from "date-fns";
import { es as esLocale, enUS as enLocale } from "date-fns/locale";
import { LanguageProviderContext } from "@/providers/LanguageProvider";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

interface DateSelectorProps {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  formatString?: string;
  view?: "year";
}

const MonthYearPicker = ({
  selectedDate,
  onSelect,
  locale,
}: {
  selectedDate: Date;
  onSelect: (date: Date) => void;
  locale: Locale;
}) => {
  const [currentYear, setCurrentYear] = useState(
    (selectedDate || new Date()).getFullYear(),
  );

  const months = Array.from({ length: 12 }, (_, i) => {
    return new Date(currentYear, i, 1);
  });

  return (
    <div className="p-3 w-[18rem]">
      <div className="flex justify-between items-center w-full px-2 mb-2">
        <button
          onClick={() => setCurrentYear((y) => y - 1)}
          aria-label="Previous year"
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "h-7 w-7 p-0 hover:bg-accent hover:text-accent-foreground",
          )}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="text-sm font-medium">{currentYear}</div>
        <button
          onClick={() => setCurrentYear((y) => y + 1)}
          aria-label="Next year"
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "h-7 w-7 p-0 hover:bg-accent hover:text-accent-foreground",
          )}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {months.map((monthDate, index) => {
          const isSelected =
            selectedDate?.getFullYear() === currentYear &&
            selectedDate?.getMonth() === index;
          return (
            <button
              key={index}
              onClick={() => onSelect(monthDate)}
              className={cn(
                "h-10 text-sm rounded-md transition-colors font-medium flex items-center justify-center capitalize",
                isSelected
                  ? "bg-primary text-primary-foreground hover:bg-primary/90 focus:bg-primary"
                  : "hover:bg-accent hover:text-accent-foreground",
              )}
            >
              {format(monthDate, "MMM", { locale }).replace(/\./g, "")}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DateSelector: React.FC<DateSelectorProps> = ({
  selectedDate,
  setSelectedDate,
  formatString = "PPP",
  view,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const { userLanguage } = useContext(LanguageProviderContext);
  const currentLocale = userLanguage === "es" ? esLocale : enLocale;

  return (
    <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center space-x-2 cursor-pointer"
        >
          <Calendar className="h-4 w-4" />
          <span>
            {format(selectedDate, view === "year" ? "yyyy-MM" : formatString, {
              locale: currentLocale,
            })}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        {view === "year" ? (
          <MonthYearPicker
            selectedDate={selectedDate}
            onSelect={(date) => {
              setSelectedDate(date);
              setIsCalendarOpen(false);
            }}
            locale={currentLocale}
          />
        ) : (
          <CalendarComponent
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate || new Date()}
            locale={currentLocale}
            onSelect={(date) => {
              if (date) {
                setSelectedDate(date);
                setIsCalendarOpen(false);
              }
            }}
            initialFocus
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

export default DateSelector;
